import api from '../../services/api';
import { EmailSignature } from './useData';

export const moduleConfig = {
  module: {
    title: 'Signatures',
    description: '',
    i18nKey: 'email-signatures',
    route: {
      list: '/email-signatures',
      new: '/email-signatures/new',
      edit: (id: number) => `/email-signatures/${id}/edit`,
      view: (id: number) => `/email-signatures/${id}`,
    },
  },
  api: {
    list: '/emails/signatures/manage',
    one: (id: number) => `/emails/signatures/${id}`,
    create: '/emails/signatures',
    update: (id: number) => `/emails/signatures/${id}`,
    delete: (id: number) => `/emails/signatures/${id}`,
  },
  emptyStates: {
    noData: 'No signatures yet. Create a signature and assign which users can use it.',
    noSearchResults: 'No signatures match your search.',
  },
  delete: {
    title: 'Delete signature',
    message: 'Are you sure you want to delete this signature?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete signature.',
    handler: async (id: number) => {
      await api.delete(`/emails/signatures/${id}`);
    },
  },
};

export const getAssignedUsersLabel = (sig: EmailSignature, maxNames = 3): string => {
  const names = sig.assignedUsers?.map((u) => u.name) ?? [];
  if (names.length === 0) return 'No users assigned';
  if (names.length <= maxNames) return names.join(', ');
  return `${names.slice(0, maxNames).join(', ')} +${names.length - maxNames} more`;
};

export const getAssignedAccountsLabel = (sig: EmailSignature, maxNames = 2): string => {
  const labels = sig.assignedAccounts?.map((a) => a.label) ?? [];
  if (labels.length === 0) return 'No accounts';
  if (labels.length <= maxNames) return labels.join(', ');
  return `${labels.slice(0, maxNames).join(', ')} +${labels.length - maxNames} more`;
};

/** Regular users may edit when they own the signature or are the sole assignee. */
export const canUserSelfEditSignature = (sig: EmailSignature): boolean =>
  sig.canSelfEdit === true ||
  (sig.assignedUserIds?.length === 1 && (sig.assignedUsers?.length ?? 0) === 1);

/** Owner or sole assignee may edit; multi-user assignees are view-only. */
export const canUserEditSignature = (
  sig: EmailSignature,
  currentUserId?: number | null,
): boolean => {
  if (sig.isViewOnly === true) return false;
  if (sig.canEdit === false) return false;
  if (sig.canEdit === true) return true;
  if (currentUserId != null && sig.createdByUserId === currentUserId) return true;
  return canUserSelfEditSignature(sig);
};
